/**
 * Dishes list route (Organizers only)
 * Browse dishes with their computed nutrition
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDishes, useDeleteDish } from '@/hooks/useDishes';
import { requireOrganizer } from '@/lib/auth/routeGuards';
import { Loading } from '@/components/ui/Loading';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { EmptyState } from '@/components/ui/EmptyState';
import { NutritionSummary } from '@/components/dishes/NutritionSummary';
import { useToast } from '@/components/ui/ToastProvider';

export const Route = createFileRoute('/dishes/')({
  beforeLoad: requireOrganizer,
  component: DishesListComponent,
});

function DishesListComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useDishes(search || undefined);
  const deleteDish = useDeleteDish();

  const dishes = data?.dishes ?? [];

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(t('dishes.deleteConfirm', { name }))) return;
    try {
      await deleteDish.mutateAsync(id);
      toast.success(t('dishes.deleted'));
    } catch {
      toast.error(t('dishes.errorDeleting'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('dishes.title')}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {t('dishes.description')}
            </p>
          </div>
          <Link
            to="/dishes/new"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            + {t('dishes.create')}
          </Link>
        </div>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('dishes.searchPlaceholder')}
          className="block w-full sm:w-96 mb-6 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />

        {isLoading && <Loading />}

        {error && (
          <ErrorMessage
            title={t('dishes.errorLoading')}
            message={error instanceof Error ? error.message : t('errors.generic')}
          />
        )}

        {!isLoading && !error && dishes.length === 0 && (
          <EmptyState
            title={t('dishes.noDishes')}
            description={t('dishes.noDishesDescription')}
          />
        )}

        {dishes.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dishes.map((dish) => (
              <div
                key={dish.id}
                className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">{dish.name}</h3>
                  {dish.goalOverride && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700">
                      {t(`goal.${dish.goalOverride}`)}
                    </span>
                  )}
                </div>

                <p className="text-xs text-gray-500 mb-3">
                  {t('dishes.ingredientsCount', {
                    count: dish.ingredients.length,
                  })}
                </p>

                <NutritionSummary
                  nutrition={dish.nutrition.perServing}
                  title={t('dishes.perServing')}
                  className="mb-4"
                />

                <div className="mt-auto flex items-center gap-3 text-sm">
                  <button
                    onClick={() => navigate({ to: `/dishes/${dish.id}` })}
                    className="font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    {t('dishes.view')}
                  </button>
                  <button
                    onClick={() =>
                      navigate({ to: `/dishes/${dish.id}/edit` })
                    }
                    className="font-medium text-gray-600 hover:text-gray-900"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={() => handleDelete(dish.id, dish.name)}
                    className="ml-auto font-medium text-red-600 hover:text-red-800"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
